import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Stack, Container } from '@mui/material';
import {
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  Menu,
  MenuItem,
  Tooltip,
  Loader,
  Avatar,
  InlineAlert,
  Icon,
  DocumentsIcon,
  NoSearchResultIcon,
  TextField,
  Modal,
  Select,
  InputLabel,
  KeyValuePair,
  ColumnLayout,
  RowLayout,
  ScrollLayout,
  Stepper,
  Snackbar,
  BarChart,
  DatePicker,
} from '@/components/atoms';
import { useSnackbar } from '@/hooks';
import { useDebounce } from '@/hooks/useDebounce';
import { getTraceId, deepEqual } from '@/utils';
import {
  Autocomplete,
  Card,
  CardInfo,
  Table,
  Tabs,
  TabPanel,
  TabsV2,
  TabPanelV2,
  Grid,
  Header,
  Footer,
  Breadcrumb,
  Sidebar,
  Graph,
  Input,
  Accordion,
  AccordionHeader,
  Toast,
  ChatMessage,
  ToggleView,
  Search,
  FileUploader,
  Dialogue,
  SingleSelect,
  DateRangePicker,
  EmptyState,
} from '@/components/molecules';
import { GridItem } from '@/components/molecules/Grid/Grid';
import { CreditScoreBlock } from '@/components/organisms/CreditScoreBlock';
import { DocumentCard } from '@/components/organisms/DocumentCard';
import { DocumentListView } from '@/components/organisms/DocumentListView';
import { EditDocumentCategoryModal } from '@/components/organisms/EditDocumentCategoryModal';
import { Filter } from '@/components/organisms/Filter';
import { StatCard } from '@/components/organisms/StatCard';
import { Form } from '@/components/organisms/Form';
import { DateRangeDropdown } from '@/components/organisms/DateRangeDropdown';
import { ErrorBoundary } from './ErrorBoundary';
import { DocumentUploadManager } from './organisms';

/**
 * API configuration for component-driven service calls
 */
export interface ApiConfig<TResult = unknown> {
  service: string;
  mapper?: string;
  args?: Record<string, unknown>;
  mapperArgs?: Record<string, unknown>;
  onDataLoaded: (response: TResult | null) => void;
  /** Optional loader handler - called with true when API starts, false when it completes (success or error) */
  handleLoader?: (loading: boolean) => void;
  /** Optional dependencies array - when provided, API will refetch when any dependency changes */
  dependencies?: unknown[];
  /** Optional debounce flag - when true, debounces API calls during continuous dependency changes (default: false) */
  debouncedRecall?: boolean;
  /** Optional prerequisite flag - if provided and false, API will not run until it becomes true */
  prerequisite?: boolean;
}

/**
 * Component configuration type for config-driven UI
 */
export interface ComponentConfig {
  component: string;
  props?: Record<string, unknown>;
  children?: (ComponentConfig | string | null | undefined)[];
  api?: ApiConfig<unknown>[];
}

/**
 * ApiComponent - Handles API calls and triggers callback with response
 * This component executes the service, applies mapper, and calls onDataLoaded with the result
 * Uses global Snackbar via useSnackbar hook to show errors
 *
 * Behavior:
 * - Without dependencies: Fetches once on mount (original behavior)
 * - With dependencies: Refetches whenever dependencies change (new behavior)
 * - With debouncedRecall: Debounces API calls during continuous dependency changes
 */
const ApiComponent: React.FC<{
  apiConfig: ApiConfig<unknown>;
}> = ({ apiConfig }) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [dependencyTrigger, setDependencyTrigger] = useState(0);
  const { showSnackbar } = useSnackbar();

  // Determine if we should use dependency-based fetching
  const hasDependencies = apiConfig.dependencies !== undefined;
  const shouldDebounce = apiConfig.debouncedRecall === true && hasDependencies;

  // Use refs to store previous values for deep comparison
  const prevDependenciesRef = useRef<unknown[] | undefined>(undefined);
  const prevServiceRef = useRef<string>(apiConfig.service);
  const prevPrerequisiteRef = useRef<boolean | undefined>(
    apiConfig.prerequisite
  );
  const isApiCallInProgressRef = useRef<boolean>(false);

  // Create a dependency tracker that updates when dependencies actually change (deep comparison)
  // Also tracks prerequisite changes for dependency-based calls
  useEffect(() => {
    if (hasDependencies && apiConfig.dependencies) {
      // Check if dependencies actually changed (deep comparison, not just reference)
      const dependenciesChanged =
        prevDependenciesRef.current === undefined ||
        !deepEqual(prevDependenciesRef.current, apiConfig.dependencies);

      // Check if prerequisite changed from false to true
      const prerequisiteChanged =
        prevPrerequisiteRef.current !== apiConfig.prerequisite;
      const prerequisiteBecameTrue =
        apiConfig.prerequisite && prerequisiteChanged;

      if (dependenciesChanged) {
        prevDependenciesRef.current = apiConfig.dependencies;
        setDependencyTrigger((prev) => prev + 1);
      } else if (prerequisiteBecameTrue) {
        // Prerequisite became true - trigger API call
        prevPrerequisiteRef.current = apiConfig.prerequisite;
        setDependencyTrigger((prev) => prev + 1);
      } else {
        // Update prerequisite ref even if it didn't change (to track current state)
        if (prerequisiteChanged) {
          prevPrerequisiteRef.current = apiConfig.prerequisite;
        }
      }
    } else if (!hasDependencies && apiConfig.prerequisite !== undefined) {
      // For non-dependency calls, just track prerequisite changes
      const prerequisiteChanged =
        prevPrerequisiteRef.current !== apiConfig.prerequisite;
      if (prerequisiteChanged) {
        prevPrerequisiteRef.current = apiConfig.prerequisite;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiConfig.dependencies,
    apiConfig.prerequisite,
    hasDependencies,
    apiConfig.service,
  ]);

  // Debounce the dependency trigger if debouncedRecall is enabled
  const debouncedTrigger = useDebounce(dependencyTrigger, 500);

  // Use debounced trigger if enabled, otherwise use regular trigger
  const effectTrigger = shouldDebounce ? debouncedTrigger : dependencyTrigger;

  // Use a stable prerequisite value to prevent unnecessary effect re-runs
  const stablePrerequisite = apiConfig.prerequisite;

  useEffect(() => {
    // Check prerequisite - if provided and false, don't run the API
    if (stablePrerequisite !== undefined && !stablePrerequisite) {
      return;
    }

    // If no dependencies, only run once using hasLoaded flag
    if (!hasDependencies && hasLoaded) {
      return;
    }

    // Skip initial render if debouncing (dependencyTrigger will be 0 on first render)
    if (shouldDebounce && dependencyTrigger === 0) {
      return;
    }

    // For dependency-based calls, check if we're already making a call
    if (hasDependencies && isApiCallInProgressRef.current) {
      return;
    }

    // Check if service or prerequisite changed (for non-dependency based calls)
    if (!hasDependencies) {
      const serviceChanged = prevServiceRef.current !== apiConfig.service;
      const prerequisiteChanged =
        prevPrerequisiteRef.current !== apiConfig.prerequisite;

      if (serviceChanged) {
        prevServiceRef.current = apiConfig.service;
        setHasLoaded(false); // Reset so API can be called again with new service
      }
      if (prerequisiteChanged) {
        prevPrerequisiteRef.current = apiConfig.prerequisite;
        // If prerequisite changed from false to true, allow API call
        if (apiConfig.prerequisite && !prevPrerequisiteRef.current) {
          setHasLoaded(false);
        }
      }

      // Skip if already loaded and nothing changed
      if (!serviceChanged && !prerequisiteChanged && hasLoaded) {
        return;
      }
    }

    const fetchData = async () => {
      // Mark that API call is in progress
      isApiCallInProgressRef.current = true;

      // Trigger loader handler with true when API starts
      if (apiConfig.handleLoader) {
        apiConfig.handleLoader(true);
      }

      try {
        // Import executeService dynamically to avoid circular dependency
        const { executeService } = await import('@/services');

        // Execute service with mapper
        const result = await executeService<unknown>({
          service: apiConfig.service,
          args: apiConfig.args || {},
          mapper: apiConfig.mapper,
          mapperArgs: apiConfig.mapperArgs || {},
        });

        // Call the callback with the result
        apiConfig.onDataLoaded(result);
        setHasLoaded(true);
        isApiCallInProgressRef.current = false;

        // Trigger loader handler with false when API completes successfully
        if (apiConfig.handleLoader) {
          apiConfig.handleLoader(false);
        }
      } catch (err: unknown) {
        // Error is already normalized by the interceptor with statuscode and message
        const error = err as Error & {
          statuscode?: number;
          code?: string | number;
          details?: unknown;
        };

        const statusCode = error.statuscode;
        const errorMessage = error.message || 'An unknown error occurred';

        // Log structured error with trace ID for distributed tracing
        console.error(
          JSON.stringify({
            t: new Date().toISOString(),
            severity: 'ERROR',
            message: 'API call failed',
            error: error.name || 'UnknownError',
            statuscode: statusCode,
            errorMessage,
            stack_trace: error.stack?.replace(/\n/g, '|') || null,
            trace_id: getTraceId(),
          })
        );

        // Show error using global Snackbar
        const snackbarMessage = statusCode
          ? `(${statusCode}) ${errorMessage}`
          : errorMessage;

        showSnackbar(snackbarMessage, {
          variant: 'error',
          autoHideDuration: 6000,
        });

        // Call with empty/error state
        apiConfig.onDataLoaded(null);
        setHasLoaded(true);
        isApiCallInProgressRef.current = false;

        // Trigger loader handler with false when API completes with error
        if (apiConfig.handleLoader) {
          apiConfig.handleLoader(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // If dependencies provided, use effectTrigger (debounced or not); otherwise use hasLoaded flag
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(hasDependencies ? [effectTrigger] : [hasLoaded]),
    apiConfig.service,
    stablePrerequisite, // Use stable value to prevent unnecessary re-runs
  ]);

  // ApiComponent no longer renders anything - Snackbar is handled globally
  return null;
};

/**
 * Component registry mapping component names to React components
 * Components are imported from atoms, molecules, and organisms folders
 */
export const componentRegistry: Record<string, React.ElementType> = {
  // Atoms
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  Menu,
  MenuItem,
  Tooltip,
  Loader,
  Avatar,
  InlineAlert,
  Icon,
  DocumentsIcon,
  NoSearchResultIcon,
  TextField,
  Modal,
  Select,
  InputLabel,
  KeyValuePair,
  ColumnLayout,
  RowLayout,
  ScrollLayout,
  Stepper,
  Snackbar,
  BarChart,
  DatePicker,

  // Molecules
  DateRangePicker,
  EmptyState,
  Card,
  CardInfo,
  Table,
  Tabs,
  TabPanel,
  TabsV2,
  TabPanelV2,
  Grid,
  GridItem,
  Header,
  Footer,
  Breadcrumb,
  Sidebar,
  Graph,
  Input,
  Accordion,
  AccordionHeader,
  Toast,
  ChatMessage,
  ToggleView,
  Search,
  FileUploader,
  Dialogue,
  SingleSelect,
  Autocomplete,

  // Organisms
  CreditScoreBlock,
  DocumentCard,
  DocumentListView,
  DocumentUploadManager,
  EditDocumentCategoryModal,
  Filter,
  StatCard,
  Form,

  // Utility Components
  ErrorBoundary,
  DateRangeDropdown,

  // MUI Layout components (used directly as they're layout utilities)
  Box,
  Paper,
  Stack,
  Container,
};

/**
 * Recursively render components from configuration
 * @param config - Component configuration object, string, or null/undefined
 * @param index - Index for React key prop
 * @param skipErrorComponents - If true, skip failed components; if false, show error message
 * @returns React.ReactNode - Rendered component or null
 */
export const renderComponent = (
  config: ComponentConfig | string | null | undefined,
  index: number,
  skipErrorComponents: boolean = true,
  snackbarShow?: (
    message: string,
    options?: {
      variant?: 'error' | 'success' | 'warning' | 'info';
      autoHideDuration?: number;
    }
  ) => void
): React.ReactNode => {
  // Handle null or undefined
  if (!config) {
    return null;
  }

  // If it's a string, just return it as text
  if (typeof config === 'string') {
    return config;
  }

  // Validate config has required properties
  if (!config.component) {
    return null;
  }

  const { component, props = {}, children = [], api } = config;

  const Component = componentRegistry[component];

  if (!Component) {
    return null;
  }

  // Handle special props that need to be rendered as components
  const processedProps = { ...props };

  // If header prop is a ComponentConfig, render it
  if (
    processedProps.header &&
    typeof processedProps.header === 'object' &&
    'component' in processedProps.header
  ) {
    processedProps.header = renderComponent(
      processedProps.header as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // If label prop is a ComponentConfig, render it (for KeyValuePair, etc.)
  if (
    processedProps.label &&
    typeof processedProps.label === 'object' &&
    'component' in processedProps.label
  ) {
    processedProps.label = renderComponent(
      processedProps.label as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // If value prop is a ComponentConfig, render it (for KeyValuePair, etc.)
  if (
    processedProps.value &&
    typeof processedProps.value === 'object' &&
    'component' in processedProps.value
  ) {
    processedProps.value = renderComponent(
      processedProps.value as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // If icon prop is a ComponentConfig render it
  if (
    processedProps.icon &&
    typeof processedProps.icon === 'object' &&
    'component' in processedProps.icon
  ) {
    // Handle single ComponentConfig
    processedProps.icon = renderComponent(
      processedProps.icon as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // If buttonStartIcon prop is a ComponentConfig, render it
  if (
    processedProps.buttonStartIcon &&
    typeof processedProps.buttonStartIcon === 'object' &&
    'component' in processedProps.buttonStartIcon
  ) {
    processedProps.buttonStartIcon = renderComponent(
      processedProps.buttonStartIcon as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // Recursively render children, filtering out null values
  const renderedChildren = children
    .map((child, idx) =>
      renderComponent(child, idx, skipErrorComponents, snackbarShow)
    )
    .filter((child) => child !== null);

  // If API config is present, add ApiComponent(s) to the beginning of children
  const apiComponents =
    api && api.length > 0
      ? api.map((apiConfig, apiIndex) =>
          React.createElement(ApiComponent, {
            key: `api-${apiIndex}`,
            apiConfig,
          })
        )
      : [];

  // Wrap component creation in try-catch to handle creation errors
  try {
    const componentElement = React.createElement(
      Component,
      { key: index, ...processedProps },
      ...apiComponents,
      ...renderedChildren
    );

    // Wrap with ErrorBoundary to catch runtime errors
    const errorFallback = skipErrorComponents
      ? null
      : React.createElement(
          Typography,
          {
            sx: {
              color: 'error.main',
              padding: '8px',
              border: '1px solid',
              borderColor: 'error.main',
              borderRadius: '4px',
              fontSize: '12px',
            },
          },
          `Error in ${component}`
        );

    return React.createElement(
      ErrorBoundary as React.ComponentType<{
        componentName?: string;
        fallback?: React.ReactNode;
        children?: React.ReactNode;
      }>,
      {
        key: `error-boundary-${index}`,
        componentName: component,
        fallback: errorFallback,
      },
      componentElement
    );
  } catch (error) {
    // Log structured error with trace ID for distributed tracing
    console.error(
      JSON.stringify({
        t: new Date().toISOString(),
        severity: 'ERROR',
        message: `Error creating component "${component}" at index ${index}`,
        error: error instanceof Error ? error.name : 'UnknownError',
        stack_trace:
          error instanceof Error
            ? error.stack?.replace(/\n/g, '|') || null
            : null,
        trace_id: getTraceId(),
      })
    );

    // Show error using global Snackbar if available
    if (snackbarShow) {
      snackbarShow('Failed to Render', {
        variant: 'error',
        autoHideDuration: 6000,
      });
    }

    // If skipErrorComponents is true, skip the component (return null)
    if (skipErrorComponents) {
      return null;
    }

    // If skipErrorComponents is false, show error message using Typography
    return React.createElement(
      Typography,
      {
        key: index,
        sx: {
          color: 'error.main',
          padding: '8px',
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: '4px',
          fontSize: '12px',
        },
      },
      `Error creating ${component}`
    );
  }
};
